import { PeakStatus } from "../types";
import { DynamicLoops, DynamicLoopsFromAPI } from "./forms/fv-loops/types";
import { BasicPersonalData } from "./forms/personal-data/types";
import { PreSpirometryDataFromAPI } from "./forms/spirometry/types";
import { Symptoms, SymptomsDataFromAPI } from "./forms/symptoms/types";

export type Nullable<T> = T | null | undefined;

export type WithErrors<T> = {
    [K in keyof T]?: T[K] | ErrorObject;
};

export enum BooleanText {
    YES = "Yes",
    NO = "No",
}

export enum ExtendedBooleanText {
    YES = "Yes",
    NO = "No",
    UNKNOWN = "Unknown",
}

export enum TestStatus {
    COMPLETED = "Completed",
    NOT_COMPLETED = "Not completed",
}

export type ErrorObject = {
    error_message: string;
};

export enum Severity {
    UNDETERMINED = "Undetermined",
    MILD = "Mild",
    MODERATE = "Moderate",
    SEVERE = "Severe",
}

export type InterpretationDataFromAPI = {
    limiting_system: Nullable<string>;
    detailed_interpretation: Nullable<string>;
    other_limiting_systems: Array<{
        label: string;
        is_none?: boolean;
    }>;
    other_detailed_interpretations: Array<{
        label: string;
        title: string;
        is_disable_none_detailed?: boolean;
    }>;
    previous_limiting_system: Nullable<string>;
    previous_detailed_interpretation: Nullable<string>;
    severity: Severity;
};

export type InterpretationTablesFromAPI = {
    final_peak_values: PeakValues;
    slopes: Slopes;
    fv_loops: DynamicLoopsFromAPI;
    pre_spirometry: PreSpirometryDataFromAPI;
    post_spirometry: PreSpirometryDataFromAPI;
    responses: Responses;
    symptoms: SymptomsDataFromAPI;
    person: Pick<BasicPersonalData, "bmi">;
};

export type InterpretationTables = {
    final_peak_values: PeakValues;
    fv_loops: DynamicLoops;
    slopes: Slopes;
    responses: Responses;
    symptoms: Symptoms & DynamicLoops;
};

export interface BasicTableData {
    high_limit: number;
    low_limit: number;
    measured: Nullable<number>;
    label: string;
    percent_of_predicted: Nullable<number>;
    predicted: Nullable<number>;
    status: PeakStatus;
    status_color: string;
    predicted_range: Nullable<string>;
}

export type PeakValues = {
    bf: BasicTableData;
    br: BasicTableData;
    dbp: BasicTableData;
    hr: BasicTableData;
    hrr: BasicTableData;
    load: BasicTableData;
    mets: BasicTableData;
    o2pulse: BasicTableData;
    o2sat: BasicTableData;
    petco2: BasicTableData;
    peto2: BasicTableData;
    rer: BasicTableData;
    sbp: BasicTableData;
    time: BasicTableData;
    vat: BasicTableData;
    vat_prct_pred_vo2_max: BasicTableData;
    vat_prct_vo2_max: BasicTableData;
    vco2: BasicTableData;
    vd_vt: BasicTableData;
    ve: BasicTableData;
    ve_vco2: BasicTableData;
    ve_vco2_at_vat: BasicTableData;
    ve_vo2: BasicTableData;
    vo2: BasicTableData;
    vo2_kg: BasicTableData;
    vt: BasicTableData;
    ic: BasicTableData;
};

interface SlopeTableData extends BasicTableData {
    slope_calculation_begin_value: number;
    slope_calculation_end_value: number;
}

export type Slopes = {
    hr: SlopeTableData;
    ouep: SlopeTableData;
    oues: SlopeTableData;
    o2pulse: SlopeTableData;
    petco2: SlopeTableData;
    vco2_ve: SlopeTableData;
    ve: SlopeTableData;
    ve_vco2: SlopeTableData;
    vo2_ve: SlopeTableData;
    vo2_wr: SlopeTableData;
};

export type ResponseTableData = Omit<
    BasicTableData,
    "percent_of_predicted" | "predicted" | "measured"
>;

export type Responses = {
    bf: ResponseTableData;
    dbp: ResponseTableData;
    hr: ResponseTableData;
    o2pulse: ResponseTableData;
    petco2: ResponseTableData;
    peto2: ResponseTableData;
    sbp: ResponseTableData;
    vd_vt: ResponseTableData;
    ve: ResponseTableData;
    ve_vco2: ResponseTableData;
    ve_vo2: ResponseTableData;
    vo2_kg: ResponseTableData;
    vt: ResponseTableData;
};

type VATValue = Nullable<{
    vat: number;
    rcp: number;
    label: string;
    rcp_label: string;
}>;

export type VATValuesFromAPI = {
    hr: VATValue;
    rer: VATValue;
    vat_percent_predicted_vo2_max: VATValue;
    vat_percent_vo2_max: VATValue;
    vo2: VATValue;
    vo2_kg: VATValue;
    wr: VATValue;
    ve_vco2: VATValue;
};

export type VATValues = {
    vat: {
        hr?: number;
        rer?: number;
        vat_percent_predicted_vo2_max?: number;
        vat_percent_vo2_max?: number;
        vo2?: number;
        vo2_kg?: number;
        wr?: number;
        ve_vco2?: number;
    };
    rcp: {
        hr?: number;
        rer?: number;
        vat_percent_predicted_vo2_max?: number;
        vat_percent_vo2_max?: number;
        vo2?: number;
        vo2_kg?: number;
        wr?: number;
    };
    labels: {
        ve_vco2: [string, string];
        hr: [string, string];
        rer: [string, string];
        vat_percent_predicted_vo2_max: [string, string];
        vat_percent_vo2_max: [string, string];
        vo2: [string, string];
        vo2_kg: [string, string];
        wr: [string, string];
    };
};

export type AnalyticsData = {
    scale: number;
    scroll_position: number;
};

export type AnalyticsDataFromAPI = Partial<AnalyticsData> & {
    screen_timing?: {
        login: number;
    };
    test_id: number;
};
